import { replaceEqualDeep, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CONFIG } from '../config';

export type Server = 'DATACITE' | 'CITATION' | 'OPENALEX';

export interface EndpointDefinitionParams<DATA, PARAMS> {
  path: string;
  server: Server;
  schema?: DATA;
  params?: PARAMS;
}

export type EndpointDefinition<DATA, PARAMS> = Required<
  EndpointDefinitionParams<DATA, PARAMS>
>;

export function endpoint<DATA, PARAMS>(
  def: EndpointDefinitionParams<DATA, PARAMS>,
): EndpointDefinition<DATA, PARAMS> {
  return {
    schema: {} as DATA,
    params: {} as PARAMS,
    ...def,
  };
}

function buildURL(
  endpointDef: EndpointDefinition<unknown, unknown>,
  params: any,
) {
  const serverUrl = CONFIG.SERVERS[endpointDef.server];
  const url = `${serverUrl}${endpointDef.path}`;

  if (typeof params !== 'object' || !params) {
    return url;
  }

  const urlWithPathParams = Object.keys(params).reduce(
    (a, b) => a.replaceAll(`:${b}`, params[b]),
    url,
  );

  const queryParams = Object.keys(params).filter((key) => {
    return !url.includes(`:${key}`);
  });

  if (queryParams.length === 0) {
    return urlWithPathParams;
  }

  const queryParamsObject = new URLSearchParams();
  queryParams.forEach((key) => {
    queryParamsObject.append(key, params[key]);
  });

  return `${urlWithPathParams}?${queryParamsObject.toString()}`;
}

async function fetchEndpoint<DATA, PARAMS>(args: {
  endpoint: EndpointDefinition<DATA, PARAMS>;
  params?: PARAMS;
  url: string;
  key?: any;
  skipFetch?: boolean;
  onFail?: (body: string) => void;
}): Promise<DATA | null> {
  if (args.skipFetch) {
    return null;
  }

  let response: Response | null = null;
  try {
    response = await fetch(args.url, { method: 'GET' });
  } catch (error) {
    console.error(error); // eslint-disable-line no-console
    return null;
  }

  const body = await response.text();

  if (!response.ok) {
    // eslint-disable-next-line no-console
    console.error(
      `Error fetching ${args.url}: ${response.status} ${response.statusText} ${body}`,
    );
    if (args.onFail) {
      args.onFail(body || 'Error fetching data');
    }
    return null;
  }

  if (!body) {
    return null;
  }

  try {
    return JSON.parse(body) as DATA;
  } catch {
    return body as unknown as DATA;
  }
}

function getQueryObject<DATA, PARAMS>(args: {
  endpoint: EndpointDefinition<DATA, PARAMS>;
  params?: PARAMS;
  skipFetch?: boolean;
  onFail?: (body: string) => void;
}) {
  const url = buildURL(args.endpoint, args.params);

  const key = [args.params, url, args.skipFetch ? 'skip' : 'fetch'];

  return {
    queryKey: key,
    queryFn: async () => fetchEndpoint({ ...args, url, key }),
  };
}

export function useLocalGetEndpoint<DATA, PARAMS, DEFAULT = undefined>(args: {
  endpoint: EndpointDefinition<DATA, PARAMS>;
  params?: PARAMS;
  default?: DEFAULT;
  skipFetch?: boolean;
  onFail?: (body: string) => void;
}): DATA | DEFAULT {
  const query = getQueryObject({
    endpoint: args.endpoint,
    params: args.params,

    skipFetch: args.skipFetch,
    onFail: args.onFail,
  });

  const d = useSuspenseQuery({
    notifyOnChangeProps: ['data'],
    structuralSharing: (oldData, newData) => {
      return replaceEqualDeep(oldData, newData);
    },
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
    ...query,
  });

  const res = useMemo(
    () =>
      d.data
        ? (JSON.parse(JSON.stringify(d.data)) as DATA) // make a copy so that the data is not mutated
        : (args.default as DEFAULT),
    [d.data], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return res;
}
